import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  ImageBackground,
  Platform,
  useWindowDimensions,
  ScrollView,
  Modal,
  Alert,
  Linking,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import AuthService from './services/authService';
import { API_BASE_URL } from './services/api.js';
import GradientText from './Components/GradientText ';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../src/contexts/AuthContext';

interface WebLayoutProps {
  children: React.ReactNode;
}

const WebLayout: React.FC<WebLayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  const { width: windowWidth } = useWindowDimensions();
  const navigation = useNavigation();
  const route = useRoute();
  const { logout: authLogout } = useAuth();
  const isDesktop = Platform.OS === 'web';  // Show sidebar on all web platforms
  // Treat tablets (<=1024) as mobile: hamburger header + slide-over drawer and
  // full-width content, so the persistent desktop sidebar only shows on real
  // desktops (>1024). Phones and tablets share the same mobile layout.
  const isMobile = windowWidth <= 1024;

  const [userName, setUserName] = useState<string>('');
  const [balance, setBalance] = useState<number>(0);
  const [userId, setUserId] = useState<string>('');
  const [sidebarVisible, setSidebarVisible] = useState(!isMobile);  // Auto-hide on mobile
  const [logoutDialogVisible, setLogoutDialogVisible] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const userData = await AuthService.userProfile();
      const profileData = userData.data;
      setUserName(profileData.name);
      setBalance(profileData.balance);
      if (profileData?.id !== undefined && profileData?.id !== null) {
        setUserId(String(profileData.id));
      }
    } catch (error) {
      console.log('WebLayout profile error:', error);
    }
  };

  const handleGenerateToken = async () => {
    try {
      const sessionToken = await AsyncStorage.getItem('userToken');
      if (!sessionToken || !userId) {
        Alert.alert('', t('profile.missingUserInfo'));
        return;
      }
      const res = await AuthService.generateToken(
        userId,
        sessionToken,
        '600',
        '/admin/dashboard'
      );

      const url =
        (res as any)?.data?.url ||
        (res as any)?.data?.link ||
        (res as any)?.url ||
        (res as any)?.link ||
        (res as any)?.redirect_url ||
        (() => {
          const linkId = (res as any)?.data?.id || (res as any)?.data?.link_id || (res as any)?.link_id || (res as any)?.id;
          return linkId ? `${API_BASE_URL}/api/token-redirect/${linkId}` : null;
        })();

      if (url) {
        // On web, open in new tab; on native, use Linking
        if (Platform.OS === 'web') {
          window.open(url, '_blank');
        } else {
          const supported = await Linking.canOpenURL(url);
          if (supported) {
            await Linking.openURL(url);
          } else {
            Alert.alert('', t('errors.cannotOpenLink'));
          }
        }
      } else {
        Alert.alert('', t('errors.noTokenLink'));
      }
    } catch (e) {
      console.error('generateToken error:', e);
      Alert.alert('', t('errors.tokenGenerationFailed'));
    }
  };

  const handleLogout = async () => {
    try {
      // Clear ancillary keys that AuthContext.logout() doesn't manage.
      await AsyncStorage.multiRemove([
        'userName',
        'code',
        'display_name',
        'avatar_url',
        'userId',
        'userData',
      ]);

      // Update AuthContext — flips isAuthenticated to false, which causes
      // App.web.tsx to re-render with the UNauthenticated Stack.Group.
      // initialRouteName resolves to 'splashScreen' → LoginScreen.
      // DO NOT call navigation.reset({ name: 'LoginScreen' }) afterwards —
      // the authenticated group is still mounted at that instant, and React
      // Navigation throws "RESET was not handled by any navigator".
      await authLogout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const showLogoutDialog = () => {
    setLogoutDialogVisible(true);
  };

  const closeLogoutDialog = () => {
    setLogoutDialogVisible(false);
  };

  const confirmLogout = () => {
    closeLogoutDialog();
    handleLogout();
  };


  const navItems = [
    { name: 'Dashboard', labelKey: 'navigation.home', icon: require('./Assets/home.png') },
    { name: 'LiveScreen', labelKey: 'streaming.goLive', icon: require('./Assets/movie.png') },
    { name: 'ExploreScreen', labelKey: 'navigation.explore', icon: require('./Assets/search__icon.png') },
    { name: 'ChatScreen', labelKey: 'navigation.messages', icon: require('./Assets/chat_icon.png') },
    { name: 'ProfileScreen', labelKey: 'profile.profile', icon: require('./Assets/profile_icon.png') },
  ];

  const isActive = (screenName: string) => {
    return route.name === screenName;
  };

  const isActionActive = (screenName: string) => {
    return route.name === screenName;
  };

  if (!isDesktop) {
    // Mobile/tablet: render children without sidebar
    return <>{children}</>;
  }

  // Desktop: render with sidebar
  const sidebarWidth = isMobile ? 240 : 280;

  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  return (
    <View testID="web-layout" style={[styles.container, isMobile && styles.containerMobile]}>
      {/* Mobile Header Bar */}
      {isMobile && (
        <View style={styles.mobileHeader}>
          <TouchableOpacity
            style={styles.hamburgerButton}
            onPress={toggleSidebar}
          >
            <View style={styles.hamburgerLine} />
            <View style={styles.hamburgerLine} />
            <View style={styles.hamburgerLine} />
          </TouchableOpacity>
          <Image
            source={require('./Assets/main_icon.png')}
            style={styles.mobileHeaderLogo}
            resizeMode="contain"
          />
          <View style={styles.mobileHeaderRight}>
            <TouchableOpacity onPress={() => navigation.navigate('NotificationScreen' as never)}>
              <Image
                source={require('./Assets/notification_icon_without_dot.png')}
                style={styles.mobileHeaderIcon}
              />
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Overlay (Mobile Only - when sidebar is open) */}
      {isMobile && sidebarVisible && (
        <TouchableOpacity
          style={styles.overlay}
          activeOpacity={1}
          onPress={() => setSidebarVisible(false)}
        />
      )}

      {/* Sidebar */}
      {sidebarVisible && (
        <View testID="sidebar" style={[
          styles.sidebar,
          { width: sidebarWidth },
          isMobile && styles.sidebarMobile
        ]}>
          <ScrollView
            style={{ flex: 1 }}
            showsVerticalScrollIndicator={isMobile}
            contentContainerStyle={[
              styles.sidebarContent,
              !isMobile && styles.sidebarContentDesktop
            ]}
          >
          {/* Logo */}
          <View style={[styles.logoContainer, !isMobile && styles.logoContainerDesktop]}>
            <Image
              source={require('./Assets/main_icon.png')}
              style={styles.logo}
              resizeMode="contain"
            />
          </View>

          {/* User Info */}
          <View style={[styles.userSection, !isMobile && styles.userSectionDesktop]}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <Text style={[styles.userNameText, !isMobile && styles.userNameTextDesktop]} numberOfLines={1}>
                {userName || 'User'}
              </Text>
            </ScrollView>
            <TouchableOpacity onPress={() => navigation.navigate('CoinWalletScreen' as never)}>
              <ImageBackground
                source={require('./Assets/point_bg.png')}
                style={styles.balanceBackground}
                imageStyle={styles.balanceBackgroundImage}
              >
                <View style={styles.balanceContainer}>
                  <Image
                    source={require('./Assets/dollar_Icon.png')}
                    style={styles.coinIcon}
                  />
                  <Text style={styles.balanceText}>{balance}</Text>
                </View>
              </ImageBackground>
            </TouchableOpacity>
          </View>

          {/* Navigation Items */}
          <View style={styles.navSection}>
            {navItems.map((item) => (
              <TouchableOpacity
                key={item.name}
                testID={`nav-tab-${item.name}`}
                style={[styles.navItem, isActive(item.name) && styles.navItemActive]}
                onPress={() => navigation.navigate(item.name as never)}
              >
                <Image source={item.icon} style={[styles.navIcon, isActive(item.name) && styles.navIconActive]} />
                <Text style={[styles.navLabel, isActive(item.name) && styles.navLabelActive]}>
                  {t(item.labelKey)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Bottom Actions */}
          <View style={[styles.bottomActions, !isMobile && styles.bottomActionsDesktop]}>
            <TouchableOpacity style={styles.actionItem} onPress={handleGenerateToken}>
              <Image
                source={require('./Assets/casino_Icon.png')}
                style={styles.actionIcon}
              />
              <Text style={styles.actionLabel}>{t('navigation.loyaltyClub', 'Loyalty Club')}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.actionItem}
              onPress={() => navigation.navigate('AddCoinScreen' as never)}
            >
              <Image
                source={require('./Assets/dollar_coin.png')}
                style={styles.actionCoinIcon}
              />
              <Text style={[styles.actionLabel, isActionActive('AddCoinScreen') && styles.actionLabelActive]}>{t('settings.addCoins', 'Add Coins')}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              testID="nav-tab-notifications"
              style={styles.actionItem}
              onPress={() => navigation.navigate('NotificationScreen' as never)}
            >
              <Image
                source={require('./Assets/notification.png')}
                style={styles.notificationIcon}
              />
              <Text style={[styles.actionLabel, isActionActive('NotificationScreen') && styles.actionLabelActive]}>{t('navigation.notifications')}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              testID="nav-tab-terms"
              style={styles.actionItem}
              onPress={() => navigation.navigate('TermsAndConditionsScreen' as never)}
            >
              <Image
                source={require('./Assets/privacy_policy_icon.png')}
                style={styles.actionIcon}
              />
              <Text style={[styles.actionLabel, isActionActive('TermsAndConditionsScreen') && styles.actionLabelActive]}>{t('navigation.terms', 'Terms & Conditions')}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              testID="nav-logout-button"
              style={[styles.actionItem, styles.logoutAction]}
              onPress={showLogoutDialog}
            >
              <Image
                source={require('./Assets/logout_icon.png')}
                style={styles.logoutIcon}
              />
              <Text style={styles.logoutLabel}>{t('auth.logout')}</Text>
            </TouchableOpacity>
          </View>
          </ScrollView>
        </View>
      )}

      {/* Logout Confirmation Dialog */}
      <Modal
        visible={logoutDialogVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={closeLogoutDialog}
      >
        <View testID="logout-dialog" style={styles.modalOverlay}>
          <View style={styles.logoutDialog}>
            <Text style={styles.logoutTitle}>{t('auth.logout')}</Text>
            <Text style={styles.logoutMessage}>{t('auth.logoutConfirmation', 'Are you sure you want to logout?')}</Text>
            <View style={styles.logoutButtons}>
              <TouchableOpacity
                testID="logout-cancel-button"
                style={[styles.logoutButton, styles.cancelButton]}
                onPress={closeLogoutDialog}
              >
                <Text style={styles.cancelButtonText}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                testID="logout-confirm-button"
                style={[styles.logoutButton, styles.confirmButton]}
                onPress={confirmLogout}
              >
                <Text style={styles.confirmButtonText}>{t('auth.logout')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Content Area */}
      <View style={[styles.contentArea, isMobile && styles.contentAreaMobile]}>{children}</View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: '#0B0F14',
  },
  containerMobile: {
    flexDirection: 'column',
  },
  sidebar: {
    width: 280,
    backgroundColor: '#1a1f28',
    borderRightWidth: 1,
    borderRightColor: '#2a2f38',
    height: '100vh' as any,
    overflow: 'hidden' as any,
  },
  sidebarMobile: {
    position: 'absolute' as any,
    top: 65,
    left: 0,
    bottom: 0,
    height: 'auto' as any,
    zIndex: 1000,
    shadowColor: '#000',
    shadowOffset: { width: 2, height: 0 },
    shadowOpacity: 0.5,
    shadowRadius: 10,
    elevation: 10,
  },
  mobileHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#1a1f28',
    paddingHorizontal: 15,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#2a2f38',
  },
  mobileHeaderLogo: {
    width: 120,
    height: 40,
  },
  mobileHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  mobileHeaderIcon: {
    width: 32,
    height: 32,
    tintColor: '#fff',
  },
  hamburgerButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderRadius: 8,
  },
  hamburgerLine: {
    width: 22,
    height: 2,
    backgroundColor: '#fff',
    marginVertical: 2,
    borderRadius: 2,
  },
  overlay: {
    position: 'absolute' as any,
    top: 65,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 999,
  },
  sidebarContent: {
    padding: 20,
    paddingTop: 20,
    paddingBottom: 100,
  },
  sidebarContentDesktop: {
    paddingBottom: 20,
    flexGrow: 1,
  },
  logoContainer: {
    alignItems: 'center',
    marginTop: 0,
    marginBottom: 20,
    paddingBottom: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#2a2f38',
  },
  // Desktop: logo, username and coin badge form one group with equal 16px
  // spacing, so the divider under the logo is removed (the single divider sits
  // below the coin badge, before the nav items).
  logoContainerDesktop: {
    marginBottom: 16,
    paddingBottom: 0,
    borderBottomWidth: 0,
  },
  logo: {
    width: '100%',
    height: 80,
  } as any,
  userSection: {
    marginBottom: 20,
    paddingBottom: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#2a2f38',
  },
  // Desktop: username + coin badge use the SAME left inset as the nav items
  // (marginStart 20 + paddingLeft 16), so their left edges line up exactly with
  // the navigation icons below. No divider here — just clean vertical spacing:
  // logo →16 username →8 badge →20 nav.
  userSectionDesktop: {
    marginStart: 20,
    paddingLeft: 16,
    paddingRight: 16,
    marginBottom: 20,
    paddingBottom: 0,
    borderBottomWidth: 0,
  },
  userNameText: {
    fontSize: 22,
    fontFamily: 'Lato-Bold',
    color: '#fff',
    marginBottom: 10,
  },
  userNameTextDesktop: {
    marginBottom: 10,
    marginStart: 10,
  },
  balanceBackground: {
    flexDirection: 'row',
    alignItems: 'center',
    minWidth: 130,
    height: 40,
    justifyContent: 'center',
    paddingHorizontal: 14,
    alignSelf: 'flex-start',
  },
  balanceBackgroundImage: {
    resizeMode: 'stretch',
    borderRadius: 20,
  },
  balanceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  coinIcon: {
    width: 24,
    height: 24,
    resizeMode: 'contain',
    marginRight: 8,
  },
  balanceText: {
    color: 'white',
    fontSize: 18,
    fontFamily: 'Lato-Bold',
  },
  navSection: {
    marginBottom: 15,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 12,
    marginBottom: 8,
    marginStart: 20,
  },
  navItemActive: {
    backgroundColor: '#2a2f38',
  },
  navIcon: {
    width: 28,
    height: 28,
    resizeMode: 'contain',
    marginRight: 16,
    tintColor: '#fff',
  },
  // Active state: icon color only (same fixed 28x28 size + marginRight, so the
  // icon and the label after it never move).
  navIconActive: {
    tintColor: 'rgba(222, 49, 202, 1)',
  },
  navLabel: {
    fontSize: 18,
    fontFamily: 'Lato-Regular',
    color: '#999',
  },
  // Active state changes ONLY the color — same font/size/position so the label
  // never shifts between active and inactive items.
  navLabelActive: {
    color: 'rgba(222, 49, 202, 1)',
  },
  goLiveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 18,
    borderRadius: 12,
    marginBottom: 20,
    backgroundColor: 'transparent',
  } as any,
  goLiveIcon: {
    width: 28,
    height: 28,
    resizeMode: 'contain',
    marginRight: 10,
    tintColor: '#fff',
  },
  goLiveText: {
    fontSize: 18,
    fontFamily: 'Lato-Regular',
    marginTop: 0,
    color: '#999',
  },
  bottomActions: {
    marginTop: 20,
    paddingTop: 15,
    borderTopWidth: 1,
    borderTopColor: '#2a2f38',
  },
  bottomActionsDesktop: {
    marginTop: 'auto' as any,
  },
  actionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 12,
    marginBottom: 8,
    marginStart: 20,
  },
  actionIcon: {
    width: 28,
    height: 28,
    resizeMode: 'contain',
    marginRight: 16,
  },
  actionLabel: {
    fontSize: 18,
    fontFamily: 'Lato-Regular',
    color: '#999',
  },
  actionLabelActive: {
    color: 'rgba(222, 49, 202, 1)',
  },
  plusIcon: {
    fontSize: 32,
    color: '#fff',
    fontWeight: 'bold',
    marginRight: 12,
    width: 28,
    textAlign: 'center',
  },
  notificationIcon: {
    width: 28,
    height: 28,
    resizeMode: 'contain',
    marginRight: 16,
    tintColor: '#fff',
  },
  actionCoinIcon: {
    width: 28,
    height: 28,
    resizeMode: 'contain',
    marginRight: 16,
  },
  logoutAction: {
    marginTop: 12,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#2a2f38',
  },
  logoutIcon: {
    width: 28,
    height: 28,
    resizeMode: 'contain',
    marginRight: 16,
  },
  logoutLabel: {
    fontSize: 18,
    fontFamily: 'Lato-Regular',
    color: '#fff',
  },
  contentArea: {
    flex: 1,
    overflow: 'hidden' as any,
  },
  contentAreaMobile: {
    flex: 1,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoutDialog: {
    backgroundColor: '#2E2E2E',
    borderRadius: 15,
    padding: 20,
    width: '80%',
    maxWidth: 300,
    alignItems: 'center',
  },
  logoutTitle: {
    color: '#FFFFFF',
    fontSize: 20,
    fontFamily: 'Lato-Bold',
    marginBottom: 10,
  },
  logoutMessage: {
    color: '#FFFFFF',
    fontSize: 16,
    fontFamily: 'Lato-Regular',
    textAlign: 'center',
    marginBottom: 20,
  },
  logoutButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    gap: 10,
  },
  logoutButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: '#555555',
  },
  confirmButton: {
    backgroundColor: '#dc2626',
  },
  cancelButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontFamily: 'Lato-Bold',
  },
  confirmButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontFamily: 'Lato-Bold',
  },
});

export default WebLayout;
